import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import { KanbanCard as KanbanCardType } from '@/types';
import { MessageCircle, CheckCircle2, AlertCircle, Phone, FileText, Edit2, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { formatCurrency, formatDate, parseCivilDateString } from '@/lib/utils';
import { WhatsAppIntegrationService, MessageTemplate } from '@/services/whatsappIntegrationService';
import { Titulo } from '@/types';

interface WhatsAppBulkModalProps {
  isOpen: boolean;
  onClose: () => void;
  cards: KanbanCardType[];
  onSuccess?: () => void;
}

export const WhatsAppBulkModal: React.FC<WhatsAppBulkModalProps> = ({
  isOpen,
  onClose,
  cards,
  onSuccess,
}) => {
  const [message, setMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);

  // Buscar templates
  const { data: templates = [], isLoading: isLoadingTemplates } = useQuery({
    queryKey: ['whatsapp-templates'],
    queryFn: () => WhatsAppIntegrationService.getTemplates(),
    enabled: isOpen,
    select: (data) => data.filter(t => t.isActive), // Apenas templates ativos
  });

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Digite uma mensagem');
      return;
    }

    setIsSending(true);
    setSentCount(0);
    setFailedCount(0);

    let success = 0;
    let failed = 0;
    const errors: string[] = [];

    // Enviar mensagem para cada cliente
    for (const card of cards) {
      try {
        const cliente = card.cliente;
        const telefone = (cliente as any).whatsapp || (cliente as any).telefone || (cliente as any).celular;

        if (!telefone) {
          failed++;
          setFailedCount(failed);
          errors.push(`${cliente.nome}: Sem telefone cadastrado`);
          continue;
        }

        // Limpar telefone (remover caracteres não numéricos)
        const cleanPhone = telefone.replace(/\D/g, '');
        
        // Adicionar código do país se não tiver
        const phoneWithCountry = cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`;

        // Personalizar mensagem com dados do cliente
        let personalizedMessage = message;
        personalizedMessage = personalizedMessage.replace(/\{nome\}/g, cliente.nome);
        personalizedMessage = personalizedMessage.replace(/\{valor\}/g, formatCurrency(card.valorTotalEmAberto + card.valorTotalVencido));
        personalizedMessage = personalizedMessage.replace(/\{titulos\}/g, card.totalTitulos.toString());
        
        // Adicionar URLs dos boletos (um link por título)
        const boletoUrl = getBoletoUrl(card.titulos || []);
        personalizedMessage = personalizedMessage.replace(/\{url_boleto\}/g, boletoUrl);
        
        // Adicionar resumo dos títulos
        const resumoTitulos = generateTitulosResumo(card.titulos || []);
        personalizedMessage = personalizedMessage.replace(/\{resumo_titulos\}/g, resumoTitulos);

        // Enviar mensagem via API do GestorLeads
        const result = await WhatsAppIntegrationService.sendTextMessage(
          phoneWithCountry,
          personalizedMessage
        );

        if (result.success) {
          success++;
          setSentCount(success);
        } else {
          failed++;
          setFailedCount(failed);
          errors.push(`${cliente.nome}: ${result.error || 'Erro ao enviar'}`);
        }

        // Pequeno delay para não sobrecarregar a API
        await new Promise(resolve => setTimeout(resolve, 300));
      } catch (error: any) {
        console.error(`Erro ao enviar para ${card.cliente.nome}:`, error);
        failed++;
        setFailedCount(failed);
        errors.push(`${card.cliente.nome}: ${error.response?.data?.message || error.message || 'Erro desconhecido'}`);
      }
    }

    setIsSending(false);

    if (success > 0) {
      toast.success(`${success} mensagem(ns) enviada(s) com sucesso!`);
      if (onSuccess) {
        onSuccess();
      }
    }

    if (failed > 0) {
      const errorMessage = errors.length > 0 
        ? `${failed} falha(s): ${errors.slice(0, 3).join('; ')}${errors.length > 3 ? '...' : ''}`
        : `${failed} cliente(s) não puderam receber a mensagem`;
      toast.error(errorMessage, { duration: 5000 });
    }

    // Limpar mensagem após envio bem-sucedido
    if (success > 0) {
      setMessage('');
    }
  };

  const handleClose = () => {
    if (!isSending) {
      setMessage('');
      setSelectedTemplate(null);
      setIsEditing(false);
      setSentCount(0);
      setFailedCount(0);
      onClose();
    }
  };

  // Contar clientes com telefone
  const clientsWithPhone = cards.filter(
    card => (card.cliente as any).whatsapp || (card.cliente as any).telefone || (card.cliente as any).celular
  ).length;

  const clientsWithoutPhone = cards.length - clientsWithPhone;

  // Função para gerar resumo dos títulos
  const generateTitulosResumo = (titulos: Titulo[]): string => {
    if (!titulos || titulos.length === 0) {
      return 'Nenhum título encontrado.';
    }

    // Filtrar apenas títulos em atraso (vencidos ou com vencimento passado)
    const titulosAtraso = titulos.filter((t) => {
      const vencimento = parseCivilDateString(t.vencimento);
      vencimento.setHours(0, 0, 0, 0);
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      return vencimento <= hoje || t.status === 'VENCIDO';
    });

    if (titulosAtraso.length === 0) {
      return 'Nenhum título em atraso.';
    }

    const resumos = titulosAtraso.map(titulo => {
      const vencimento = formatDate(titulo.vencimento);
      const valor = formatCurrency(Number(titulo.valor));
      const obs = titulo.observacoes ? ` | Obs: ${titulo.observacoes}` : '';
      return `• Vencimento: ${vencimento} | Valor: ${valor}${obs}`;
    });

    return resumos.join('\n');
  };

  // Função para obter URLs dos boletos formatadas (um link por título)
  const getBoletoUrl = (titulos: Titulo[]): string => {
    if (!titulos || titulos.length === 0) {
      return 'Não disponível';
    }

    // Filtrar apenas títulos em atraso com URL de boleto
    const titulosAtrasoComBoleto = titulos.filter((t) => {
      const vencimento = parseCivilDateString(t.vencimento);
      vencimento.setHours(0, 0, 0, 0);
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      return (vencimento <= hoje || t.status === 'VENCIDO') && t.urlBoleto;
    });
    
    if (titulosAtrasoComBoleto.length === 0) {
      return 'Não disponível';
    }

    // Formatar cada título com seu link de boleto
    const linksFormatados = titulosAtrasoComBoleto.map(titulo => {
      const valor = formatCurrency(Number(titulo.valor));
      return `• Valor: ${valor} - ${titulo.urlBoleto}`;
    });

    return linksFormatados.join('\n');
  };

  // Template de mensagem padrão
  const defaultMessage = `Olá {nome}!

Identificamos que você possui {titulos} título(s) em aberto no valor total de {valor}.

Gostaríamos de entrar em contato para negociar a regularização.

Aguardo seu retorno.`;

  // Quando um template é selecionado, atualizar a mensagem
  useEffect(() => {
    if (selectedTemplate && !isEditing) {
      setMessage(selectedTemplate.content);
    }
  }, [selectedTemplate, isEditing]);

  const handleTemplateSelect = (template: MessageTemplate) => {
    setSelectedTemplate(template);
    setMessage(template.content);
    setIsEditing(false);
  };

  const handleUseDefault = () => {
    setSelectedTemplate(null);
    setMessage(defaultMessage);
    setIsEditing(true);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Enviar WhatsApp em Massa"
      size="xl"
    >
      <div className="space-y-6">
        {/* Resumo */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100">
              Resumo do Envio
            </h3>
            <Badge variant="default" className="bg-blue-600">
              {cards.length} cliente(s) selecionado(s)
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div className="flex items-center space-x-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {clientsWithPhone} com telefone
              </span>
            </div>
            {clientsWithoutPhone > 0 && (
              <div className="flex items-center space-x-2">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {clientsWithoutPhone} sem telefone
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Lista de Clientes */}
        <div className="max-h-60 overflow-y-auto border rounded-lg">
          <div className="divide-y">
            {cards.map((card) => {
              const telefone = (card.cliente as any).whatsapp || (card.cliente as any).telefone || (card.cliente as any).celular;
              return (
                <div
                  key={card.id}
                  className="p-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-sm text-gray-900 dark:text-white">
                        {card.cliente.nome}
                      </span>
                      {telefone ? (
                        <Badge variant="outline" className="text-xs">
                          <Phone className="h-3 w-3 mr-1" />
                          {telefone}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs text-red-600 border-red-600">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          Sem telefone
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {card.totalTitulos} título(s) - {formatCurrency(card.valorTotalEmAberto + card.valorTotalVencido)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Seleção de Template */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Template de Mensagem
            </label>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleUseDefault}
              className="text-xs"
            >
              Mensagem personalizada
            </Button>
          </div>

          {/* Lista de Templates */}
          {!isEditing && (
            <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
              {isLoadingTemplates ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  Carregando templates...
                </div>
              ) : templates.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  Nenhum template disponível
                </div>
              ) : (
                templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => handleTemplateSelect(template)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                      selectedTemplate?.id === template.id
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-l-blue-500'
                        : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <FileText className="h-4 w-4 text-gray-400" />
                          <h4 className="font-medium text-sm text-gray-900 dark:text-white">
                            {template.name}
                          </h4>
                          {selectedTemplate?.id === template.id && (
                            <Badge variant="default" className="text-xs">
                              Selecionado
                            </Badge>
                          )}
                        </div>
                        {template.category && (
                          <Badge variant="outline" className="text-xs mb-2">
                            {template.category}
                          </Badge>
                        )}
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 line-clamp-3">
                          {template.content}
                        </p>
                        {template.variables && template.variables.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {template.variables.map((variable, idx) => (
                              <Badge
                                key={idx}
                                variant="secondary"
                                className="text-xs"
                              >
                                {variable}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Editor de Mensagem */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {selectedTemplate ? 'Editar Mensagem' : 'Mensagem'}
              </label>
              {selectedTemplate && !isEditing && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  className="text-xs"
                >
                  <Edit2 className="h-3 w-3 mr-1" />
                  Editar
                </Button>
              )}
              {isEditing && selectedTemplate && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsEditing(false);
                    setMessage(selectedTemplate.content);
                  }}
                  className="text-xs"
                >
                  <X className="h-3 w-3 mr-1" />
                  Cancelar edição
                </Button>
              )}
            </div>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={defaultMessage}
              rows={6}
              className="w-full"
              disabled={!!selectedTemplate && !isEditing}
            />
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Use {'{nome}'}, {'{valor}'}, {'{titulos}'}, {'{url_boleto}'} ou {'{resumo_titulos}'} para personalizar
            </div>
          </div>
        </div>

        {/* Status de Envio */}
        {isSending && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
              <span className="text-sm text-yellow-800 dark:text-yellow-200">
                Enviando mensagens... ({sentCount + failedCount}/{cards.length})
              </span>
            </div>
          </div>
        )}

        {/* Resultado */}
        {!isSending && (sentCount > 0 || failedCount > 0) && (
          <div className="bg-gray-50 dark:bg-gray-800 border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {sentCount > 0 && (
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {sentCount} enviada(s)
                    </span>
                  </div>
                )}
                {failedCount > 0 && (
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {failedCount} falha(s)
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Aviso */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <div className="flex items-start space-x-2">
            <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5" />
            <div className="text-xs text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Informação:</p>
              <p>
                As mensagens serão enviadas automaticamente através da API do GestorLeads. 
                Não é necessário ter o WhatsApp Web/App aberto.
              </p>
            </div>
          </div>
        </div>

        {/* Botões */}
        <div className="flex items-center justify-end space-x-3 pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSending}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSend}
            disabled={isSending || !message.trim() || clientsWithPhone === 0}
            className="bg-green-500 hover:bg-green-600 text-white"
          >
            {isSending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Enviando...
              </>
            ) : (
              <>
                <MessageCircle className="h-4 w-4 mr-2" />
                Enviar para {clientsWithPhone} cliente(s)
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

